import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { LegalDocumentId } from "@autorepair/contracts";
import { adminApi, ApiError } from "../lib/api";
import { formatDate } from "../lib/money";
import { ModernButton, ModernErrorBanner, ModernField, ModernInput, ModernModal, ModernSelect, Spinner } from "./ui-modern";

const DOC_LABELS: Record<LegalDocumentId, string> = {
  "terms-of-service": "Terms",
  "privacy-policy": "Privacy",
  "data-processing-agreement": "DPA",
  "refund-policy": "Refunds",
  "copyright-policy": "Copyright",
};

const METHOD_LABELS = { login_clickwrap: "clickwrap", signup: "signup", offline: "offline" } as const;

/**
 * Platform-admin view of one shop's legal acceptances (see
 * apps/api/src/legal/), plus a fallback to record an acceptance signed
 * outside the app (paper/email) for a pilot shop.
 */
export function TenantLegalModal({ tenantId, shopName, onClose }: { tenantId: number; shopName: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const queryKey = ["admin", "tenants", tenantId, "legal"];
  const legalQuery = useQuery({ queryKey, queryFn: () => adminApi.tenantLegal(tenantId) });
  const [userId, setUserId] = useState<number | "">("");
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);

  const members = legalQuery.data?.items ?? [];
  const selected = members.find((m) => m.userId === userId);

  const recordMutation = useMutation({
    mutationFn: () =>
      adminApi.recordOfflineLegalAcceptance(tenantId, {
        userId: Number(userId),
        documents: selected!.documents.filter((d) => d.required).map((d) => d.id),
        note,
      }),
    onSuccess: () => {
      setNote("");
      setError(null);
      void queryClient.invalidateQueries({ queryKey });
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : "Could not record the acceptance."),
  });

  return (
    <ModernModal title={`Legal acceptance — ${shopName}`} onCancel={onClose} wide>
      {legalQuery.isLoading && <Spinner />}
      {legalQuery.error && <ModernErrorBanner message="Could not load legal acceptances." />}
      {members.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-[13px]">
            <thead>
              <tr className="text-left text-[#6b7a8f]">
                <th className="py-1.5 pr-3 font-semibold">User</th>
                {members[0]!.documents.map((d) => (
                  <th key={d.id} className="py-1.5 pr-3 font-semibold">
                    {DOC_LABELS[d.id]} <span className="font-normal">v{d.currentVersion}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {members.map((m) => (
                <tr key={m.userId} className="border-t border-[#e2e7ee] align-top">
                  <td className="py-2 pr-3">
                    <div className="font-semibold text-[#071a33]">{m.fullName}</div>
                    <div className="text-[#6b7a8f]">
                      {m.email} · {m.role}
                    </div>
                  </td>
                  {m.documents.map((d) => {
                    const current = d.acceptedVersion === d.currentVersion;
                    return (
                      <td key={d.id} className="py-2 pr-3">
                        {d.acceptedVersion ? (
                          <span className={current ? "text-[#15803d]" : "text-[#b45309]"}>
                            v{d.acceptedVersion}
                            <br />
                            <span className="text-[#6b7a8f]">
                              {formatDate(d.acceptedAt!)} · {METHOD_LABELS[d.method!]}
                            </span>
                          </span>
                        ) : (
                          <span className={d.required ? "text-[#b3261e]" : "text-[#8b97a8]"}>{d.required ? "Not accepted" : "—"}</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {members.length > 0 && (
        <form
          className="mt-5 border-t border-[#e2e7ee] pt-4"
          onSubmit={(e) => {
            e.preventDefault();
            recordMutation.mutate();
          }}
        >
          <h3 className="m-0 mb-1 text-[14px] font-bold text-[#071a33]">Record an offline acceptance</h3>
          <p className="m-0 mb-3 text-[12.5px] text-[#6b7a8f]">
            For a shop that signed the current documents on paper or by email. Records every document required for that user's role.
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <ModernField label="User" htmlFor="legal-offline-user" required>
              <ModernSelect id="legal-offline-user" value={userId} onChange={(e) => setUserId(e.target.value ? Number(e.target.value) : "")} required>
                <option value="">Choose…</option>
                {members.map((m) => (
                  <option key={m.userId} value={m.userId}>
                    {m.fullName} ({m.role})
                  </option>
                ))}
              </ModernSelect>
            </ModernField>
            <ModernField label="Note (what was signed, and where it's kept)" htmlFor="legal-offline-note" required>
              <ModernInput id="legal-offline-note" value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} required />
            </ModernField>
          </div>
          {error && <ModernErrorBanner message={error} />}
          <div className="mt-3 flex justify-end">
            <ModernButton type="submit" disabled={!selected || !note.trim() || recordMutation.isPending}>
              {recordMutation.isPending ? "Recording…" : "Record acceptance"}
            </ModernButton>
          </div>
        </form>
      )}
    </ModernModal>
  );
}
